import { compose } from 'redux';
import { connect } from 'react-redux';
import { reduxForm } from 'redux-form';
import { graphql } from '@apollo/client/react/hoc';
import get from 'lodash.get';
import set from 'lodash.set';

import { closeModal } from '../../../redux/modal';
import { submitUpdateAddress } from '../../../actions/screens';
import CountriesQuery from '../../../graphql/CountriesQuery';
import EditAddressForm from './EditAddressForm';
import fields from './fields';

const reduxFormData = reduxForm({
  form: 'address',
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  enableReinitialize: true,
});

/**
 * having countries list from CountriesQuery [{ value: '...', title: '...', regions: [...] }] ,
 * returns a tuple format [preparedCountries, regionsList]
 * example output:
 * [
 *  [ { value: '...', title: '...', regions: [...] } ],
 *  { US: [ { value: '...', title: '...' }, ... ] }
 * ]
 * */
const buildCountriesOptions = (countries) =>
  countries.reduce(
    ([countriesAcc, regionsMap], { code, name, regions }) => {
      const countryRegions = regions.length
        ? {
            [code]: regions.map(({ code: value, name: title }) => ({
              value,
              title,
            })),
          }
        : {};
      return [
        [...countriesAcc, { value: code, title: name }],
        { ...regionsMap, ...countryRegions },
      ];
    },
    [[], {}]
  );

const buildFields = (userCountries, selectedCountry) => {
  const [countriesOptions, regionsOptions] = buildCountriesOptions(
    userCountries
  );
  return fields.reduce((acc, field) => {
    if (field.id === 'address_country_code') {
      return [...acc, { ...field, options: countriesOptions }];
    }
    if (field.id === 'address_region_code') {
      return regionsOptions[selectedCountry]
        ? [...acc, { ...field, options: regionsOptions[selectedCountry] }]
        : acc;
    }
    return [...acc, field];
  }, []);
};

const mapStateToProps = ({ form }, ownProps) => ({
  fields: buildFields(
    get(ownProps, 'countries.user.countries', []),
    get(form, 'address.values.address_country_code')
  ),
  initialValues: fields.reduce(
    (acc, field) => ({
      ...acc,
      [field.id]: get(ownProps.address, field.propName),
    }),
    {}
  ),
  onSubmit: (address, dispatch) => {
    const { id, ...restAddress } = fields.reduce(
      (acc, field) => set(acc, field.propName, address[field.id]),
      {}
    );
    return dispatch(submitUpdateAddress({ id, address: restAddress }));
  },
});

const mapDispatchToProps = (dispatch) => ({
  closeForm: () => dispatch(closeModal()),
});

export default compose(
  graphql(CountriesQuery, {
    options: () => ({
      ssr: false, // won't be called during SSR
    }),
    name: 'countries',
  }),
  connect(mapStateToProps, mapDispatchToProps),
  reduxFormData
)(EditAddressForm);
